/**
 * Runtime config for apps/ddx.
 * OA-06 Feature Flag seam: `featureFlags` is read once at bootstrap and never
 * consulted by business logic in this OE — architectural preparedness only.
 */
export const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "https://api.axisnode.site";

export const DDX_SYSTEM_ID =
  (import.meta.env.VITE_DDX_SYSTEM_ID as string | undefined) ?? "";

export const DDX_TENANT_ID =
  (import.meta.env.VITE_DDX_TENANT_ID as string | undefined) ?? "";

/** Named Feature Flag extension point (OE-DDX-UI-001 §1.14 / OA-06). */
export type DdxFeatureFlags = {
  /** Reserved — no flag is gated by this OE. */
  reserved: boolean;
};

export function readFeatureFlags(): DdxFeatureFlags {
  const raw = import.meta.env.VITE_DDX_FEATURE_FLAGS;
  if (!raw) return { reserved: false };
  try {
    const parsed = JSON.parse(raw) as Partial<DdxFeatureFlags>;
    return { reserved: Boolean(parsed.reserved) };
  } catch {
    return { reserved: false };
  }
}

export const featureFlags = readFeatureFlags();

/**
 * Seed navigation for the canonical, cross-session Projects (ADR-DEV-DDX-UI-001
 * workaround, extended): unlike ad hoc Projects created in a browser tab, these
 * are permanent, known to every session regardless of sessionStorage. Business
 * data for each is still always read live via GET /project/{id} — this is a
 * navigation seed, not a cache. Merge with the session index in ProjectSidebar.
 */
export const KNOWN_PROJECTS: { id: string; label: string }[] = [
  { id: "37b5ced3-9ef8-44e7-b4ae-f12f24098cab", label: "CIOS" },
  { id: "80638f0f-30f3-425f-9110-7b73da98b67c", label: "Developing Dashboard (DDX)" },
  { id: "d821cf58-e232-4c5b-830d-8c02b923bf96", label: "D-65 Cognitive Engine Observability" },
  { id: "91169a7d-c628-43a7-8a72-d75c5bc77061", label: "D-66 POD Assistant: Compromisos y Captura" },
  { id: "4c1b8965-c79a-45b0-bc5a-982af9af8f24", label: "D-67 Ciclo de vida de los datos" },
  { id: "2c7d0f5e-ba31-4b02-ad59-5733f20345c7", label: "D-68 Marketplace y bibliotecas con licencia" },
  { id: "c94da53a-fdfa-4328-af0e-486c2397ebc7", label: "D-69 Capa de explicacion visual" },
];

/**
 * DocumentType ids on the shared axisnode-platform-v1 template, for the
 * Overview "profile" sections (Diseño conceptual / Lo que resuelve /
 * Módulos / Relaciones / Reglas de operación / Pendientes / Oportunidades).
 * Registered once via register_document_type — reused by name (document
 * matrix rows carry the type name, not the id) except at creation time,
 * where the id is required by create_document.
 */
export const PROFILE_DOCUMENT_TYPES = {
  conceptNote: "6f9c242c-9ba4-4bfa-aa92-355894574758",
  problemStatement: "63ecfa41-59b2-48b8-89de-dfdc2709d6a8",
  sourceTranscript: "29e47c5a-e100-4f8d-88a6-5e7ce0caed4b",
  legacyReference: "46ca2f02-48a9-4a0d-a823-ce02487f8003",
  moduleBreakdown: "9bb637f2-2606-4a7a-9227-372a5b740e83",
  systemRelations: "5874e5a4-7852-4359-a7d9-f9e42a0ef272",
  operatingRules: "75cb2132-4bda-4856-a716-a5c9e27a525d",
} as const;

/**
 * The shared axisnode-platform-v1 ProjectTemplate (ADR-002 §10) — every
 * Project created through the standard "Nuevo Project" flow reuses this by
 * default, so it inherits the full DocumentType set (Concept Note, Problem
 * Statement, Module Breakdown, System Relations, Operating Rules, Action
 * Item, Improvement Opportunity, ADR, Operational Envelope, Board
 * Resolution). Creating a genuinely new template stays available as an
 * advanced option for a process shape this one doesn't fit.
 */
export const DEFAULT_TEMPLATE_ID = "e1cb6d0c-38de-4f3d-ba63-ad349f944eb5";
